/**
 * Service Mapper Utilities
 *
 * Services are identified by `slug` on both sides of the API (contract v1
 * §3.2). The backend is authoritative for price and duration; the static
 * duration strings in data/services.js are only a fallback for display
 * when the API is unreachable.
 */

/**
 * Static duration strings (data/services.js) to minutes.
 */
export const DURATION_TO_MINUTES = {
  '15 min': 15,
  '50 min': 50,
  '1 hr': 60,
  '1 hr 15 min': 75,
  '1 hr 25 min': 85,
};

/**
 * Convert a static duration string to minutes.
 * @param {string} durationStr - e.g. "50 min", "1 hr 15 min"
 * @returns {number|null} Minutes, or null when the string is not recognised
 */
export const getServiceDuration = (durationStr) => DURATION_TO_MINUTES[durationStr] ?? null;

/**
 * Format minutes the way data/services.js writes durations.
 * @param {number} minutes
 * @returns {string} e.g. 50 -> "50 min", 60 -> "1 hr", 85 -> "1 hr 25 min"
 */
export const formatDurationMinutes = (minutes) => {
  if (!Number.isFinite(minutes) || minutes <= 0) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (!hours) return `${mins} min`;
  return mins ? `${hours} hr ${mins} min` : `${hours} hr`;
};

/**
 * Slug of a service given either the slug itself or a service object.
 * @param {string|{slug: string}} service
 * @returns {string}
 * @throws {Error} When no slug can be found
 */
export const getServiceSlug = (service) => {
  const slug = typeof service === 'string' ? service : service?.slug;
  if (!slug || typeof slug !== 'string') {
    throw new Error('Invalid service - missing slug');
  }
  return slug;
};

/**
 * Find service by slug.
 * @param {Array} services
 * @param {string} slug
 * @returns {Object|null}
 */
export const findServiceBySlug = (services, slug) =>
  (services || []).find((service) => service.slug === slug) || null;
